import type { ReactNode } from 'react'
import { Check } from 'lucide-react'
import type { Contact } from '@/api/people'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { formatActivity, initials, netOf } from './peopleStats'

export function PersonHero({ contact: c, actions }: { contact: Contact; actions?: ReactNode }) {
  const { format: exact, formatWhole } = useFormatCurrency()
  const wide = useMediaQuery(DESKTOP_QUERY)
  // A phone hero is a glance, like the other pages: whole rupees.
  const format = wide ? exact : formatWhole
  const net = netOf(c)
  const both = c.owedToUs > 0 && c.weOwe > 0
  const subtitle = c.archived
    ? 'Archived'
    : c.lastActivityOn
      ? `Last activity ${formatActivity(c.lastActivityOn)}`
      : null

  return (
    <section
      aria-label={`Balance with ${c.name}`}
      className="flex flex-col gap-3 rounded-2xl border border-[var(--fl-hero-border)] bg-gradient-to-br from-[var(--fl-hero-bg)] to-[var(--fl-hero-bg-2)] px-3.5 py-3 text-[var(--fl-hero-fg)] md:flex-row md:items-center md:gap-8 md:rounded-[20px] md:p-6"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-2.5 md:gap-5">
        <div className="flex items-center gap-2.5 md:gap-3.5">
          <span
            aria-hidden="true"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[var(--fl-primary)] text-sm font-semibold text-[var(--fl-primary-fg)] md:h-12 md:w-12 md:text-lg"
          >
            {initials(c.name)}
          </span>
          <div className="min-w-0">
            <h1 className="break-words font-serif text-[17px] font-semibold leading-tight md:text-[26px]">
              {c.name}
            </h1>
            {subtitle && (
              <p className="mt-0.5 text-xs text-[var(--fl-hero-fg-2)] md:text-[13px]">{subtitle}</p>
            )}
          </div>
        </div>

        {net === 0 && !both ? (
          <p className="flex items-center gap-2 text-sm font-medium md:text-[15px]">
            <Check size={18} strokeWidth={2.4} aria-hidden="true" />
            You’re all settled with {c.name}
          </p>
        ) : (
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:gap-10">
            <div className="flex items-baseline justify-between gap-3 md:block">
              <p className="text-[13px] text-[var(--fl-hero-fg-2)]">
                {net > 0 ? `${c.name} owes you` : net < 0 ? `You owe ${c.name}` : 'You’re even'}
              </p>
              <p
                data-testid="person-net"
                className={`shrink-0 font-serif text-[22px] font-bold tabular-nums tracking-tight md:mt-0.5 md:text-[40px] md:font-semibold ${net < 0 ? 'text-[var(--fl-hero-expense)]' : ''}`}
              >
                {format(Math.abs(net))}
              </p>
            </div>
            {both && (
              <div className="grid grid-cols-2 gap-3 border-t border-[var(--fl-hero-bar-faint)] pt-2 md:flex md:gap-10 md:border-0 md:pb-1.5 md:pt-0">
                <div>
                  <p className="text-xs text-[var(--fl-hero-fg-2)]">Owed to you</p>
                  <p className="mt-0.5 font-semibold tabular-nums text-[var(--fl-hero-income)]">
                    {format(c.owedToUs)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[var(--fl-hero-fg-2)]">You owe {c.name}</p>
                  <p className="mt-0.5 font-semibold tabular-nums text-[var(--fl-hero-expense)]">
                    {format(c.weOwe)}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
        {both && (
          <p className="-mt-1 text-xs text-[var(--fl-hero-fg-2)] md:-mt-2">
            Settling up nets the two, so only {format(Math.abs(net))} changes hands.
          </p>
        )}
      </div>
      {actions}
    </section>
  )
}
